import { airlineLocator } from './traveler_view';

/**
 * Só letras e no máximo 4 — o formato de rótulo de marca/canal ("VOLL"), não de
 * uma confirmação de fornecedor (que sempre carrega dígito ou é mais longa).
 */
const BRAND_SHAPE = /^[A-Z]{1,4}$/i;

/**
 * O código de TERCEIRO (confirmação do hotel, da locadora) quando ele é crível —
 * senão `null`, e a tela OMITE a linha em vez de mostrar um valor que o balcão não
 * reconhece. Em prod o `reservationNumber` de uma estadia veio "VOLL", o nome do
 * canal (voucher VOLL-H17721028, issue #4256). Descarta: o próprio `voucherCode`
 * ou o prefixo dele, o nome do `supplier` e o formato de rótulo de marca. A
 * comparação é aparada e case-insensitive, como o `refKey` da reconciliação.
 */
export function plausibleSupplierCode(
  code: string | null | undefined,
  voucher: { supplier?: string | null; voucherCode?: string | null },
): string | null {
  const value = code?.trim();
  if (!value || BRAND_SHAPE.test(value)) return null;
  const key = value.toUpperCase();
  const voucherCode = voucher.voucherCode?.trim().toUpperCase();
  if (voucherCode && (key === voucherCode || key === voucherCode.split('-')[0])) return null;
  if (key === voucher.supplier?.trim().toUpperCase()) return null;
  return value;
}

/** O `reservationNumber` do voucher pela mesma régua — o que o documento de voucher,
 *  o passe da carteira e a reconciliação leem, para que os três concordem. */
export function supplierConfirmation(voucher: {
  reservationNumber?: string | null;
  supplier?: string | null;
  voucherCode?: string | null;
}): string | null {
  return plausibleSupplierCode(voucher.reservationNumber, voucher);
}

/** Um PNR só de letras: 6, como todo PNR. */
const PNR_LETTERS = /^[A-Z]{6}$/i;

/**
 * O código de reserva de um VOO (localizador) quando ele é crível — senão `null`, e a
 * tela do viajante OMITE a linha (#6368). Em prod o localizador de um voo cancelado
 * veio "RESERVADO": o STATUS da reserva no fornecedor no lugar do código, e o e-mail
 * em inglês o mostrou como código. Um PNR tem 6 letras/dígitos; o código longo de
 * NDC/consolidador ("LA9570787SVGR") sempre carrega dígito. Sem dígito, então, só o
 * formato de PNR passa — o que derruba qualquer status ("RESERVADO", "A CONFIRMAR",
 * "N/A", "-") e o rótulo de marca ("VOLL") sem lista de palavras para manter.
 */
export function plausibleBookingCode(code: string | null | undefined): string | null {
  const value = code?.trim();
  if (!value) return null;
  return /\d/.test(value) || PNR_LETTERS.test(value) ? value : null;
}

/**
 * O localizador que o VIAJANTE vê num trecho de voo (#6368): a escolha do
 * `airlineLocator` entre os candidatos que são código de reserva
 * (`plausibleBookingCode`). Status do fornecedor num campo de localizador
 * ("RESERVADO") conta como ausente — o código real do outro campo assume; sem
 * nenhum, a tela omite a linha. O `airlineLocator` segue cru para a telemetria e
 * para a comparação de bilhetes separados.
 */
export function travelerFacingLocator(segment: {
  locator?: string | null;
  companyLocator?: string | null;
}): string | null {
  return airlineLocator({
    locator: plausibleBookingCode(segment.locator),
    companyLocator: plausibleBookingCode(segment.companyLocator),
  });
}
